import {useMemo, useContext} from "react";
import {useGetEnrolledCourses, EnrolledCourseDto} from "@/app2/shared/data";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";

interface UseEnrolledCoursesResult {
	enrolledCourses: EnrolledCourseDto[] | null;
	isLoading: boolean;
	isError: boolean;
	error: Error | null;
	refetch: () => void;
}

/**
 * Feature-specific hook to fetch user's enrolled courses
 * 
 * Uses the shared useGetEnrolledCourses hook and adds HomePage-specific logic:
 * - Filters to active courses only (no completed, no soft deleted)
 * - Maps API data to UI format
 * - Provides feature-specific interface
 * 
 * This is a vertical slice hook - it lives with the HomePage feature
 * because it contains business logic specific to the "Enrolled Courses" section
 */
export const useEnrolledCourses = (): UseEnrolledCoursesResult => {
	const [{locale}] = useContext(LocaleContext);
	const resolvedLocale = resolveLocale(locale);

	// Use the shared React Query hook with HomePage-specific filters and locale for i18n
	const {data, isLoading, isError, error, refetch} = useGetEnrolledCourses({
		includeCompleted: false, // UI only shows active courses
		includeSoftDeleted: false, // UI only shows active courses
		locale: resolvedLocale,
	});

	// Transform API data to UI format (memoized for performance)
	const enrolledCourses = useMemo((): EnrolledCourseDto[] | null => {
		if (!data?.items) return null;
		// Use explicit type to avoid TS inference issues with generated types
		return data.items;
	}, [data]);

	// Enrolled-courses cache is invalidated from WebSocketProvider on completion events
	// (`completedCourseEnrollments`, `completedPlaylistEnrollments`, `completedUnenrollments`).

	return {
		enrolledCourses,
		isLoading,
		isError,
		error: error as Error | null,
		refetch
	};
};

